import {
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Typography
} from "@mui/material"
import Button from "@mui/material/Button";
import React, {useEffect, useState} from "react";
import {LoginSocialFacebook} from "reactjs-social-login";
import {useQueryClient} from "react-query";
import {Alert} from "@mui/lab";
import {useTranslation} from "react-i18next";
import {AdAccount} from "crqlar-react-common/dist/models/meta/adAccount";
import {FacebookConfig} from "crqlar-react-common/dist/models/meta/facebookConfig";
import {FacebookService} from "crqlar-react-common/dist/services/meta/facebook.service";

interface Props {
    open: boolean;
    onClose: () => void;
    onConfirm: (selectedAdAccount: AdAccount | undefined) => void;
    facebookConfig: FacebookConfig | undefined;
}

const ChangeAdAccountDialog = (props: Props) => {

    const [selectedAdAccount, setSelectedAdAccount] = useState<AdAccount | undefined>(undefined);
    const [adAccounts, setAdAccounts] = useState<AdAccount[]>([]);
    const [isLoadingAdAccounts, setIsLoadingAdAccounts] = useState(false);
    const [isErrorAdAccounts, setIsErrorAdAccounts] = useState(false);
    const [authorizationDone, setAuthorizationDone] = useState(false);
    const {t} = useTranslation()
    const queryClient = useQueryClient();

    useEffect(() => {
        if (props.facebookConfig?.adAccountId) {
            const adAccount: AdAccount = {
                id: props.facebookConfig.adAccountId,
                name: props.facebookConfig.adAccountName
            };
            setSelectedAdAccount(adAccount);
        }
    }, [props.facebookConfig]);

    const handleSelectAdAccount = (value: AdAccount | undefined) => {
        setSelectedAdAccount(value);
    };

    const handleChangeFbUser = () => {
        console.log('logout')
        FacebookService.changeFacebookUser().then(() => {
            queryClient.invalidateQueries(['facebookConfig']);
        });
    }

    const handleSuccessAuthorize = (response: any) => {
        setAuthorizationDone(true);
        const newConfig: FacebookConfig = {
            appId: props.facebookConfig?.appId,
            appSecret: props.facebookConfig?.appSecret,
            accessToken: response?.data?.accessToken,
            enabled: props.facebookConfig?.enabled,
        };
        FacebookService.saveConfig(newConfig).then(() => {
            queryClient.invalidateQueries(['facebookConfig']);
        });
    };

    const onReject = (response: any) => {

    }

    useEffect(() => {
        fetchAdAccounts();
    }, [props.facebookConfig?.accessToken, props.facebookConfig?.adAccountId]);

    const fetchAdAccounts = async () => {
        setIsLoadingAdAccounts(true);
        setIsErrorAdAccounts(false);
        try {
            const accounts = await FacebookService.getAdAccounts();
            setAdAccounts(accounts);
        } catch (error) {
            setIsErrorAdAccounts(true);
        } finally {
            setIsLoadingAdAccounts(false);
        }
    };

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            fullWidth
        >
            <DialogTitle>{t('Change Ad Account')}</DialogTitle>
            <DialogContent sx={{p: 4}}>
                <Box>
                    <FormControl fullWidth sx={{mt: 2}}>
                        <InputLabel id="ad-account-select-label">{t('Choose Ad Account')}</InputLabel>
                        <Select
                            disabled={isErrorAdAccounts}
                            labelId="ad-account-select-label"
                            value={selectedAdAccount?.id === 'No Account Selected' as string ? adAccounts[0] : selectedAdAccount}
                            onChange={(e) => handleSelectAdAccount(e.target.value as AdAccount)}
                            label={t("Choose Ad Account")}
                            renderValue={(selected) =>
                                selected ? (
                                    <Box>
                                        <Typography variant="body1" sx={{fontWeight: 650}}>
                                            {(selected as AdAccount).name}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            sx={{fontWeight: 600, paddingY: 1, fontSize: 14}}
                                        >
                                            {(selected as AdAccount).id}
                                        </Typography>
                                    </Box>
                                ) : ""
                            }
                        >
                            {adAccounts.length > 0 && adAccounts.map((adAccount: any) => (
                                <MenuItem key={adAccount.id} value={adAccount}>
                                    <Box>
                                        <Typography variant="body1" sx={{fontWeight: 650}}>
                                            {adAccount.name}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            sx={{fontWeight: 600, paddingY: 1, fontSize: 14}}
                                        >
                                            {adAccount.id}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Grid container spacing={5}>
                        <Grid item md={6}>
                            <Button
                                sx={{mt: 3}}
                                onClick={() => props.onConfirm(selectedAdAccount?.id === 'No Account Selected' as string ? adAccounts[0] : selectedAdAccount)}
                                variant={'contained'}
                                disabled={adAccounts.length < 1}
                            >
                                {t('Change Ad Account')}
                            </Button>
                        </Grid>

                        {
                            (authorizationDone && props.facebookConfig?.adAccountId && props.facebookConfig?.adAccountName) && (
                                <Grid item md={6} sx={{mt: 3}}>
                                    <Alert severity="success">
                                        <Typography variant="h6">Authorization successful!</Typography>
                                    </Alert>
                                </Grid>
                            )
                        }

                        {isLoadingAdAccounts && <CircularProgress/>}
                        {isErrorAdAccounts && (
                            <Grid item md={6} sx={{mt: 3}}>
                                <Alert severity="error">
                                    <Typography variant="h6">{t('Facebook login failed!')}</Typography>
                                </Alert>
                            </Grid>
                        )}

                    </Grid>

                </Box>
            </DialogContent>
            <DialogActions sx={{justifyContent: 'space-between', p: 2}}>
                <Button onClick={props.onClose}>{t('Close')}</Button>

                {
                    props.facebookConfig?.appId ?
                        (
                            <LoginSocialFacebook
                                appId={props.facebookConfig.appId as string}
                                scope="ads_management,business_management"
                                onResolve={handleSuccessAuthorize}
                                onLogoutSuccess={handleChangeFbUser}
                                onReject={(error: any) => {
                                    onReject(error)
                                }}
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    sx={{backgroundColor: '#21cbf5'}}
                                >
                                    {t('Change Facebook User')}
                                </Button>
                            </LoginSocialFacebook>
                        )
                        :
                        // If app id is not defined - display an error
                        <Alert severity="error" sx={{ width: '50%' }}>
                            <Typography variant="h6">{t('Meta app id missing!')}</Typography>
                        </Alert>
                }

            </DialogActions>
        </Dialog>
    )
}

export default ChangeAdAccountDialog
